import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import type { ConversationThread, ThreadMessage } from '@/types/omniChannel';
import { calculateUnreadCount, markThreadAsViewed } from '@/types/omniChannel';
import { useCallback } from 'react';

// Strip non-digits and keep the last 10 — matches phone numbers regardless of
// formatting (+61 418 906 795 vs +61418906795 vs 0418 906 795).
const last10 = (p?: string | null) => (p ?? '').replace(/\D/g, '').slice(-10) || 'unknown';

interface UseConversationThreadsOptions {
  search?: string;
  unreadOnly?: boolean;
  limit?: number;
}

// Fetch all conversation threads sorted by most recent message
export function useConversationThreads(options: UseConversationThreadsOptions = {}) {
  const { search, unreadOnly = false, limit = 50 } = options;

  return useQuery({
    queryKey: ['omni-channel-threads', { search, unreadOnly }],
    queryFn: async (): Promise<ConversationThread[]> => {
      // Fetch SMS messages with related data
      const { data: smsMessages, error: smsError } = await supabase
        .from('sms_messages')
        .select(`
          id,
          content,
          direction,
          sent_at,
          status,
          media_urls,
          from_number,
          to_number,
          lead_id,
          customer_id,
          tracking_number,
          campaign_id,
          sender_id,
          leads:lead_id(customer_name, lead_number, customer_phone, customer_email),
          customers:customer_id(customer_name, primary_phone, primary_email),
          campaigns:campaign_id(name),
          profiles!sms_messages_sender_id_fkey(full_name)
        `)
        .order('sent_at', { ascending: false })
        .limit(500);

      if (smsError) throw smsError;

      // Fetch Email messages with related data
      const { data: emailMessages, error: emailError } = await supabase
        .from('email_messages')
        .select(`
          id,
          content,
          subject,
          direction,
          sent_at,
          status,
          attachment_urls,
          from_email,
          to_email,
          lead_id,
          customer_id,
          sender_id,
          opened_count,
          clicked_count,
          bounce_reason,
          leads:lead_id(customer_name, lead_number, customer_phone, customer_email),
          customers:customer_id(customer_name, primary_phone, primary_email),
          profiles!email_messages_sender_id_fkey(full_name)
        `)
        .order('sent_at', { ascending: false })
        .limit(500);

      if (emailError) {
        console.error('Email fetch error:', emailError);
        // Continue with SMS only if email table doesn't exist yet
      }

      // Group messages into threads
      const threadMap = new Map<string, ConversationThread>();

      // Process SMS messages
      for (const msg of smsMessages || []) {
        // Group by lead/customer so the thread isn't split by stored-format
        // differences in tracking_number (NULL vs '+61…' vs '08…').
        const threadKey = msg.lead_id
          ? `lead:${msg.lead_id}`
          : msg.customer_id
            ? `customer:${msg.customer_id}`
            : `phone:${last10(msg.direction === 'inbound' ? msg.from_number : msg.to_number)}`;

        const contactName = msg.leads?.customer_name || msg.customers?.customer_name || 'Unknown';
        const contactPhone = msg.leads?.customer_phone || msg.customers?.primary_phone ||
          (msg.direction === 'inbound' ? msg.from_number : msg.to_number) || '';
        const contactEmail = msg.leads?.customer_email || msg.customers?.primary_email || undefined;

        if (!threadMap.has(threadKey)) {
          threadMap.set(threadKey, {
            id: threadKey,
            lead_id: msg.lead_id || undefined,
            customer_id: msg.customer_id || undefined,
            contact_name: contactName,
            contact_phone: contactPhone,
            contact_email: contactEmail,
            lead_number: msg.leads?.lead_number || undefined,
            campaign_name: msg.campaigns?.name || undefined,
            tracking_number: msg.tracking_number || undefined,
            last_message: msg.content || '',
            last_message_at: msg.sent_at,
            last_message_direction: msg.direction as 'inbound' | 'outbound',
            last_message_type: 'sms' as const,
            unread_count: 0,
            messages: [],
          });
        }

        const thread = threadMap.get(threadKey)!;
        // Loop is desc-by-sent_at, so the first non-null we see wins —
        // ensures replies go out from the most recently used tracking number
        // even if the most recent message itself was inbound with no tracking_number.
        if (!thread.tracking_number && msg.tracking_number) {
          thread.tracking_number = msg.tracking_number;
        }
        const message: ThreadMessage = {
          id: msg.id,
          type: 'sms',
          direction: msg.direction as 'inbound' | 'outbound',
          content: msg.content || '',
          timestamp: msg.sent_at,
          sender_name: (msg as any).profiles?.full_name || undefined,
          from_number: msg.from_number || undefined,
          to_number: msg.to_number || undefined,
          status: (msg.status as ThreadMessage['status']) || undefined,
          media_urls: (msg.media_urls as string[]) || undefined,
        };
        thread.messages.push(message);
      }

      // Process Email messages
      for (const msg of emailMessages || []) {
        // Use the same key shape as SMS so a lead's SMS + email collapse into one thread.
        const threadKey = msg.lead_id
          ? `lead:${msg.lead_id}`
          : msg.customer_id
            ? `customer:${msg.customer_id}`
            : `email:${(msg.direction === 'inbound' ? msg.from_email : msg.to_email)?.toLowerCase() || 'unknown'}`;

        const contactName = msg.leads?.customer_name || msg.customers?.customer_name || 'Unknown';
        const contactPhone = msg.leads?.customer_phone || msg.customers?.primary_phone || '';
        const contactEmail = msg.leads?.customer_email || msg.customers?.primary_email ||
          (msg.direction === 'inbound' ? msg.from_email : msg.to_email) || undefined;

        if (!threadMap.has(threadKey)) {
          threadMap.set(threadKey, {
            id: threadKey,
            lead_id: msg.lead_id || undefined,
            customer_id: msg.customer_id || undefined,
            contact_name: contactName,
            contact_phone: contactPhone,
            contact_email: contactEmail,
            lead_number: msg.leads?.lead_number || undefined,
            campaign_name: undefined,
            tracking_number: undefined,
            last_message: msg.subject || msg.content || '',
            last_message_at: msg.sent_at,
            last_message_direction: msg.direction as 'inbound' | 'outbound',
            last_message_type: 'email' as const,
            unread_count: 0,
            messages: [],
          });
        }

        const thread = threadMap.get(threadKey)!;
        
        // Update thread's last message if this email is more recent
        if (new Date(msg.sent_at) > new Date(thread.last_message_at)) {
          thread.last_message = msg.subject || msg.content || '';
          thread.last_message_at = msg.sent_at;
          thread.last_message_direction = msg.direction as 'inbound' | 'outbound';
          thread.last_message_type = 'email';
        }

        const message: ThreadMessage = {
          id: msg.id,
          type: 'email',
          direction: msg.direction as 'inbound' | 'outbound',
          content: msg.content || '',
          timestamp: msg.sent_at,
          sender_name: (msg as any).profiles?.full_name || undefined,
          from_email: msg.from_email || undefined,
          to_email: msg.to_email || undefined,
          subject: msg.subject || undefined,
          status: (msg.status as ThreadMessage['status']) || undefined,
          media_urls: (msg.attachment_urls as string[]) || undefined,
          opened_count: msg.opened_count || undefined,
          clicked_count: msg.clicked_count || undefined,
          bounce_reason: msg.bounce_reason || undefined,
        };
        thread.messages.push(message);
      }

      // Convert to array
      let threads = Array.from(threadMap.values());

      // Sort messages within each thread chronologically (oldest first)
      threads.forEach(thread => {
        thread.messages.sort((a, b) => 
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
        // Calculate unread count using localStorage
        thread.unread_count = calculateUnreadCount(thread);
      });

      // Sort by last message timestamp (most recent first)
      threads.sort((a, b) =>
        new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime()
      );

      // Apply search filter
      if (search) {
        const searchLower = search.toLowerCase();
        threads = threads.filter(
          t =>
            t.contact_name.toLowerCase().includes(searchLower) ||
            t.contact_phone.includes(search) ||
            t.contact_email?.toLowerCase().includes(searchLower) ||
            t.lead_number?.toLowerCase().includes(searchLower) ||
            t.last_message.toLowerCase().includes(searchLower)
        );
      }

      // Apply unread filter
      if (unreadOnly) {
        threads = threads.filter(t => t.unread_count > 0);
      }

      return threads.slice(0, limit);
    },
    staleTime: 30000, // 30 seconds
  });
}

// Hook to mark a thread as read
export function useMarkThreadRead() {
  const queryClient = useQueryClient();

  return useCallback((threadId: string) => {
    markThreadAsViewed(threadId);
    // Invalidate queries to recalculate unread counts
    queryClient.invalidateQueries({ queryKey: ['omni-channel-threads'] });
    queryClient.invalidateQueries({ queryKey: ['unread-messages-count'] });
  }, [queryClient]);
}

// Hook to get messages for a specific thread
export function useThreadMessages(threadId: string | null) {
  return useQuery({
    queryKey: ['thread-messages', threadId],
    queryFn: async (): Promise<ThreadMessage[]> => {
      if (!threadId) return [];

      // Parse thread ID — keys are now `lead:<id>`, `customer:<id>`, or `phone:<last10>`
      const [type, id] = threadId.split(':');
      const messages: ThreadMessage[] = [];

      // Fetch SMS messages
      let smsQuery = supabase
        .from('sms_messages')
        .select(`
          id,
          content,
          direction,
          sent_at,
          status,
          media_urls,
          from_number,
          to_number,
          sender_id,
          profiles!sms_messages_sender_id_fkey(full_name)
        `)
        .order('sent_at', { ascending: true });

      if (type === 'lead') {
        smsQuery = smsQuery.eq('lead_id', id);
      } else if (type === 'customer') {
        smsQuery = smsQuery.eq('customer_id', id);
      } else if (type === 'phone') {
        // Match either side by last-10 digits so format variants collapse together.
        smsQuery = smsQuery.or(`from_number.ilike.%${id},to_number.ilike.%${id}`);
      }

      const { data: smsData, error: smsError } = await smsQuery;
      if (smsError) throw smsError;

      // Add SMS messages
      for (const msg of smsData || []) {
        messages.push({
          id: msg.id,
          type: 'sms' as const,
          direction: msg.direction as 'inbound' | 'outbound',
          content: msg.content || '',
          timestamp: msg.sent_at,
          sender_name: (msg as any).profiles?.full_name || undefined,
          from_number: msg.from_number || undefined,
          to_number: msg.to_number || undefined,
          status: (msg.status as ThreadMessage['status']) || undefined,
          media_urls: (msg.media_urls as string[]) || undefined,
        });
      }

      // Fetch Email messages (only for lead/customer threads, not phone-based)
      if (type === 'lead' || type === 'customer') {
        let emailQuery = supabase
          .from('email_messages')
          .select(`
          id,
          content,
          subject,
          direction,
          sent_at,
          status,
          attachment_urls,
          from_email,
          to_email,
          sender_id,
          opened_count,
          clicked_count,
          bounce_reason,
          profiles!email_messages_sender_id_fkey(full_name)
        `)
          .order('sent_at', { ascending: true });

        if (type === 'lead') {
          emailQuery = emailQuery.eq('lead_id', id);
        } else {
          emailQuery = emailQuery.eq('customer_id', id);
        }

        const { data: emailData, error: emailError } = await emailQuery;
        
        if (!emailError && emailData) {
          for (const msg of emailData) {
            messages.push({
              id: msg.id,
              type: 'email' as const,
              direction: msg.direction as 'inbound' | 'outbound',
              content: msg.content || '',
              timestamp: msg.sent_at,
              sender_name: (msg as any).profiles?.full_name || undefined,
              from_email: msg.from_email || undefined,
              to_email: msg.to_email || undefined,
              subject: msg.subject || undefined,
              status: (msg.status as ThreadMessage['status']) || undefined,
              media_urls: (msg.attachment_urls as string[]) || undefined,
              opened_count: msg.opened_count || undefined,
              clicked_count: msg.clicked_count || undefined,
              bounce_reason: msg.bounce_reason || undefined,
            });
          }
        }
      }

      // Sort all messages by timestamp
      messages.sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      return messages;
    },
    enabled: !!threadId,
  });
}
